import React from 'react';

// Default company / payment details shown on the invoice. The company name and
// logo are taken from the order's organization when available; these are the
// fallbacks. Edit here to change the static branding.
const DEFAULT_COMPANY = {
    name: 'Shafa Care',
    addressLines: ['Dhaka', 'Bangladesh'],
    phone: '+880 153-139-0647',
};

const CURRENCY_SYMBOLS = { USD: '$', BDT: '৳', EUR: '€', GBP: '£', INR: '₹' };

// Shared style tokens
const INK = '#1a1a1a';
const MUTED = '#555555';
const RULE = '#cfcfcf';
const TABLE_BORDER = '#dcdcdc';
const COL_DIVIDER = '#ececec';

const Invoice = React.forwardRef(({ order }, ref) => {
    if (!order) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    // Extract number from Mongo $numberDecimal format or a plain number
    const getNumberValue = (value) => {
        if (!value) return 0;
        if (typeof value === 'object' && value.$numberDecimal) {
            return Number(value.$numberDecimal);
        }
        return Number(value) || 0;
    };

    const getTotals = () => {
        if (order?.amounts) {
            return {
                subtotal: getNumberValue(order?.amounts?.subtotal),
                discount: getNumberValue(order?.amounts?.discount),
                total: getNumberValue(order?.amounts?.total),
                paid: getNumberValue(order?.amounts?.paid),
                due: getNumberValue(order?.amounts?.due),
                tax: getNumberValue(order?.amounts?.tax),
            };
        }
        return { subtotal: 0, discount: 0, total: 0, paid: 0, due: 0, tax: 0 };
    };

    const totals = getTotals();

    // The real discount is the sum of per-item discounts; amounts.discount is order-level (usually 0).
    const itemDiscountTotal = (order?.orderItems || []).reduce((sum, it) => sum + getNumberValue(it?.discount), 0);
    const totalDiscount = itemDiscountTotal + totals.discount;
    // amounts.subtotal is already net of per-item discounts — add them back for the gross (pre-discount) value.
    const grossSubtotal = totals.subtotal + itemDiscountTotal;

    // Percentages shown next to Discount / Service Tax labels (like the reference invoice)
    const discountPct = grossSubtotal > 0 ? (totalDiscount / grossSubtotal) * 100 : 0;
    const taxBase = totals.subtotal;
    const taxPct = taxBase > 0 ? (totals.tax / taxBase) * 100 : 0;
    // Balance due = total − paid (clamped at 0). Don't fall back to `total` when due is 0,
    // otherwise a fully-paid invoice would show the whole amount as still owed.
    const balanceDue = Math.max(totals.total - totals.paid, 0);

    // Currency symbol (only Total & Balance Due are shown with a symbol, matching the reference)
    const symbol = CURRENCY_SYMBOLS[order?.currency] || '';
    const money = (n) => n.toFixed(2);
    const moneyWithSymbol = (n) => `${symbol}${n.toFixed(2)}`;

    // Company / branding — prefer the order's organization, fall back to defaults
    const org = order?.organization || {};
    const companyName = org?.name || DEFAULT_COMPANY.name;
    const companyAddressLines = DEFAULT_COMPANY.addressLines;
    const companyPhone = DEFAULT_COMPANY.phone;

    // A summary row in the totals block (label left, value right)
    const summaryRow = (label, value, opts = {}) => (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: opts.strong ? '11px 4px' : '9px 4px',
                fontSize: opts.strong ? '15px' : '13.5px',
                fontWeight: opts.strong ? 'bold' : 400,
                color: INK,
                borderTop: opts.topBorder ? '1px solid #b0b0b0' : 'none',
            }}
        >
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );

    const th = (label, align = 'left', width, last = false) => (
        <th
            style={{
                padding: '10px 12px',
                textAlign: align,
                fontWeight: 'bold',
                fontSize: '13px',
                color: INK,
                borderBottom: `1px solid ${TABLE_BORDER}`,
                borderRight: last ? 'none' : `1px solid #e6e6e6`,
                width,
            }}
        >
            {label}
        </th>
    );

    const tdStyle = (align = 'left', last = false) => ({
        padding: '12px',
        fontSize: '13px',
        color: INK,
        verticalAlign: 'top',
        textAlign: align,
        borderRight: last ? 'none' : `1px solid ${COL_DIVIDER}`,
    });

    return (
        <div
            ref={ref}
            style={{
                backgroundColor: '#ffffff',
                maxWidth: '820px',
                margin: '0 auto',
                padding: '48px 56px',
                fontFamily: 'Arial, Helvetica, sans-serif',
                color: INK,
            }}
        >
            {/* Top header: brand (left) + company name & address (right) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '27px', fontWeight: 'bold', color: '#4b2e9e', letterSpacing: '0.5px' }}>
                        {companyName}
                    </span>
                </div>
                <div style={{ flex: 1, textAlign: 'right', lineHeight: 1.55 }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: INK }}>{companyName}</p>
                    {companyAddressLines.map((line, i) => (
                        <p key={i} style={{ margin: 0, fontSize: '13.5px', color: '#444444' }}>
                            {line}
                        </p>
                    ))}
                </div>
            </div>

            {/* Rule with INVOICE inside it */}
            <div style={{ display: 'flex', alignItems: 'center', margin: '26px 0' }}>
                <div style={{ flex: 1.9, borderTop: `1px solid ${RULE}` }} />
                <div style={{ padding: '0 20px', fontSize: '21px', letterSpacing: '2px', color: '#333333' }}>INVOICE</div>
                <div style={{ flex: 1, borderTop: `1px solid ${RULE}` }} />
            </div>

            {/* Bill To (left) + invoice details grid (right) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '34px' }}>
                <div style={{ maxWidth: '46%' }}>
                    <p style={{ fontSize: '14px', color: '#333333', margin: '0 0 9px 0' }}>Bill To</p>
                    <p style={{ fontSize: '14.5px', fontWeight: 'bold', color: INK, margin: '0 0 5px 0', lineHeight: 1.5 }}>
                        {order.billTo?.name || 'N/A'}
                    </p>
                    {order.billTo?.address && (
                        <p style={{ fontSize: '13.5px', color: '#333333', margin: '2px 0', lineHeight: 1.55, whiteSpace: 'pre-line' }}>
                            {order.billTo.address}
                        </p>
                    )}
                    {order.billTo?.email && (
                        <p style={{ fontSize: '13.5px', color: '#333333', margin: '2px 0', lineHeight: 1.55 }}>{order.billTo.email}</p>
                    )}
                    {order.billTo?.phone && (
                        <p style={{ fontSize: '13.5px', color: '#333333', margin: '2px 0', lineHeight: 1.55 }}>{order.billTo.phone}</p>
                    )}
                </div>

                <div style={{ width: '42%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            {(order.customInvoiceNumber || order.orderNumber) && (
                                <tr>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', fontWeight: 'bold', color: INK, width: '46%' }}>Invoice#</td>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', color: '#333333' }}>{order.customInvoiceNumber || order.orderNumber}</td>
                                </tr>
                            )}
                            <tr>
                                <td style={{ padding: '5px 0', fontSize: '13.5px', fontWeight: 'bold', color: INK }}>Invoice Date</td>
                                <td style={{ padding: '5px 0', fontSize: '13.5px', color: '#333333' }}>{formatDate(order?.createdAt)}</td>
                            </tr>
                            {order.status && (
                                <tr>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', fontWeight: 'bold', color: INK }}>Status</td>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', fontWeight: 'bold', color: INK, textTransform: 'capitalize' }}>{order.status}</td>
                                </tr>
                            )}
                            {order.latestPayment?.method && (
                                <tr>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', fontWeight: 'bold', color: INK }}>Payment Method</td>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', color: '#333333', textTransform: 'capitalize' }}>
                                        {String(order.latestPayment.method).replace(/_/g, ' ')}
                                    </td>
                                </tr>
                            )}
                            {order.terms && (
                                <tr>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', fontWeight: 'bold', color: INK }}>Terms</td>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', color: '#333333' }}>{order.terms}</td>
                                </tr>
                            )}
                            {order.dueDate && (
                                <tr>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', fontWeight: 'bold', color: INK }}>Due Date</td>
                                    <td style={{ padding: '5px 0', fontSize: '13.5px', color: '#333333' }}>{formatDate(order.dueDate)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Items table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${TABLE_BORDER}`, marginBottom: '18px' }}>
                <thead>
                    <tr style={{ background: '#f4f4f5' }}>
                        {th('#', 'left', '5%')}
                        {th('Item', 'left')}
                        {th('Qty', 'right', '11%')}
                        {th('Rate', 'right', '13%')}
                        {th('Discount', 'right', '13%')}
                        {th('Amount', 'right', '14%', true)}
                    </tr>
                </thead>
                <tbody>
                    {order?.orderItems && order?.orderItems?.length > 0 ? (
                        order.orderItems.map((item, index) => {
                            const qty = Number(item?.quantity) || 0;
                            const rate = getNumberValue(item?.unitPrice);
                            const discount = getNumberValue(item?.discount);
                            const itemTotal = getNumberValue(item.total) || qty * rate - discount;
                            const unit = item?.unit || item?.meta?.unit || '';

                            return (
                                <tr key={item._id || index}>
                                    <td style={tdStyle('left')}>{index + 1}</td>
                                    <td style={tdStyle('left')}>
                                        <div>{item?.name || 'N/A'}</div>
                                        {item?.description && (
                                            <div style={{ color: INK, fontSize: '12px', fontWeight: 'bold', marginTop: '3px', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                                                {item.description}
                                            </div>
                                        )}
                                    </td>
                                    <td style={tdStyle('right')}>
                                        {qty.toFixed(2)}
                                        {unit && <div style={{ color: MUTED }}>{unit}</div>}
                                    </td>
                                    <td style={tdStyle('right')}>{money(rate)}</td>
                                    <td style={tdStyle('right')}>{money(discount)}</td>
                                    <td style={tdStyle('right', true)}>{money(itemTotal)}</td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan="6" style={{ padding: '32px 16px', textAlign: 'center', color: MUTED }}>
                                No items
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Totals (right aligned) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '44px' }}>
                <div style={{ width: '44%' }}>
                    {summaryRow('Sub Total', money(grossSubtotal))}
                    {totalDiscount > 0 &&
                        summaryRow(
                            `Discount${discountPct > 0 ? ` (${discountPct.toFixed(2)}%)` : ''}`,
                            `(-) ${money(totalDiscount)}`
                        )}
                    {totals.tax > 0 &&
                        summaryRow(`Service Tax${taxPct > 0 ? ` (${taxPct.toFixed(3)}%)` : ''}`, money(totals.tax))}
                    {summaryRow('Total', moneyWithSymbol(totals.total), { strong: true, topBorder: true })}
                    {totals.paid > 0 && summaryRow('Paid', `(-) ${moneyWithSymbol(totals.paid)}`)}
                    {summaryRow('Balance Due', moneyWithSymbol(balanceDue), { strong: true, topBorder: true })}
                </div>
            </div>

            {/* When fully paid, thank the customer; otherwise show payment instructions. */}
            {String(order?.paymentStatus).toLowerCase() === 'paid' ? (
                <div style={{ marginBottom: '56px' }}>
                    <p style={{ fontSize: '14.5px', color: INK, margin: 0, lineHeight: 1.7 }}>
                        It's a pleasure doing business with you.
                    </p>
                    <p style={{ fontSize: '14.5px', fontWeight: 'bold', color: INK, margin: '4px 0 0 0', lineHeight: 1.7 }}>
                        Thank you!
                    </p>
                </div>
            ) : (
                <div style={{ marginBottom: '56px' }}>
                    <p style={{ fontSize: '14.5px', color: INK, margin: '0 0 18px 0' }}>
                        Accepted payment methods
                    </p>
                    <p style={{ fontSize: '13.5px', color: INK, margin: '0 0 6px 0', lineHeight: 1.7 }}>
                        Please pay within 7 days.
                    </p>
                    <p style={{ fontSize: '13.5px', color: INK, margin: 0, lineHeight: 1.7 }}>
                        Bkash: <strong>{companyPhone}</strong>
                    </p>
                </div>
            )}

            {/* Footer */}
            <div style={{ borderTop: `1px solid ${RULE}`, paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <p style={{ fontSize: '13px', color: MUTED, margin: '0 0 3px 0' }}>{companyName}</p>
                    <p style={{ fontSize: '13px', color: MUTED, margin: 0 }}>Contact with us: {companyPhone}</p>
                </div>
                {(order.createdBy?.name || order.updatedBy?.name) && (
                    <div style={{ textAlign: 'right' }}>
                        {order.updatedBy?.name && String(order.updatedBy?.id) !== String(order.createdBy?.id) && (
                            <p style={{ fontSize: '12.5px', color: MUTED, margin: 0 }}>
                                Updated by: <span style={{ color: INK }}>{order.updatedBy.name}</span>
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

Invoice.displayName = 'Invoice';

export default Invoice;
